import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { categoryAPI, CreateCategoryRequest, UpdateCategoryRequest } from '../services/api';
import { useCategories, getCategoryIcon } from '../contexts/CategoryContext';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  TagIcon,
  XMarkIcon,
  CheckIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const PRESET_COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#A855F7', '#EC4899', '#6B7280'
];

// Icon options: store icon names in database, map to emojis in frontend
// This avoids database encoding issues with emojis
const PRESET_ICONS: { name: string; emoji: string }[] = [
  { name: 'food', emoji: '🍔' },
  { name: 'transportation', emoji: '🚗' },
  { name: 'entertainment', emoji: '🎬' },
  { name: 'utilities', emoji: '💡' },
  { name: 'healthcare', emoji: '🏥' },
  { name: 'shopping', emoji: '🛍️' },
  { name: 'education', emoji: '📚' },
  { name: 'other', emoji: '📦' },
  { name: 'housing', emoji: '🏠' },
  { name: 'savings', emoji: '💰' },
  { name: 'gaming', emoji: '🎮' },
  { name: 'travel', emoji: '✈️' },
  { name: 'dining', emoji: '🍕' },
  { name: 'coffee', emoji: '☕' },
  { name: 'gifts', emoji: '🎁' },
  { name: 'payments', emoji: '💳' },
  { name: 'groceries', emoji: '🛒' },
  { name: 'fitness', emoji: '💪' },
  { name: 'pets', emoji: '🐾' },
  { name: 'beauty', emoji: '💄' },
  { name: 'hobbies', emoji: '🎯' },
  { name: 'art', emoji: '🎨' },
  { name: 'music', emoji: '🎵' },
  { name: 'subscriptions', emoji: '📱' },
  { name: 'electronics', emoji: '💻' },
  { name: 'maintenance', emoji: '🔧' },
  { name: 'taxes', emoji: '📋' },
  { name: 'charity', emoji: '❤️' },
  { name: 'business', emoji: '💼' },
  { name: 'party', emoji: '🎉' },
];

const Categories: React.FC = () => {
  const { categories, loading, error: contextError, refreshCategories } = useCategories();
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ReturnType<typeof useCategories>['categories'][0] | null>(null);
  const [formData, setFormData] = useState<CreateCategoryRequest>({ name: '', icon: '', color: '' });

  // Combined error from context and local
  const displayError = error || contextError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        const updateData: UpdateCategoryRequest = {};
        if (formData.name) updateData.name = formData.name;
        if (formData.icon) updateData.icon = formData.icon;
        if (formData.color) updateData.color = formData.color;
        await categoryAPI.update(editingCategory.id, updateData);
      } else {
        await categoryAPI.create(formData);
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', icon: '', color: '' });
      refreshCategories(); // Refresh from context
    } catch (err) {
      setError('Failed to save category');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await categoryAPI.delete(id);
      refreshCategories(); // Refresh from context
    } catch (err) {
      setError('Failed to delete category. It may be in use by existing expenses.');
      console.error(err);
    }
  };

  const openEditModal = (category: typeof categories[0]) => {
    setEditingCategory(category);
    setFormData({ name: category.name, icon: category.icon || '', color: category.color || '' });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', icon: 'other', color: '#6B7280' });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center animate-pulse shadow-lg shadow-indigo-500/30">
            <TagIcon className="h-8 w-8 text-white" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">Loading categories...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        variants={fadeInUp}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <TagIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Categories</h1>
            <p className="text-gray-500 dark:text-gray-400">{categories.length} categories available</p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={openCreateModal}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-xl transition-all"
        >
          <PlusIcon className="h-5 w-5" />
          Add Category
        </motion.button>
      </motion.div>

      {/* Error Message */}
      <AnimatePresence>
        {displayError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800"
          >
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-red-700 dark:text-red-300">{displayError}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <XMarkIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Categories Grid */}
      <motion.div
        variants={staggerContainer}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        {categories.map((category, index) => (
          <motion.div
            key={category.id}
            variants={fadeInUp}
            transition={{ delay: index * 0.03 }}
            whileHover={{ scale: 1.02, y: -2 }}
            className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-5 border border-gray-200/50 dark:border-gray-700/50 shadow-lg group relative overflow-hidden"
          >
            {/* Color accent bar */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ backgroundColor: category.color || '#6B7280' }}
            />

            {/* Action buttons */}
            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(category)}
                className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleDelete(category.id)}
                className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
              </motion.button>
            </div>

            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl shadow-lg"
                style={{
                  backgroundColor: `${category.color || '#6B7280'}20`,
                  boxShadow: `0 10px 25px -5px ${category.color || '#6B7280'}30`
                }}
              >
                {getCategoryIcon(category)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 dark:text-white truncate">{category.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {category.is_default && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-600 dark:text-gray-300">
                      <SparklesIcon className="h-3 w-3" />
                      Default
                    </span>
                  )}
                  <div
                    className="w-4 h-4 rounded-full border-2 border-white dark:border-gray-700 shadow-sm"
                    style={{ backgroundColor: category.color || '#6B7280' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {categories.length === 0 && (
        <motion.div
          variants={fadeInUp}
          className="text-center py-16"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
            <TagIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No categories yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Get started by adding your first category.</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-xl transition-all"
          >
            Add First Category
          </motion.button>
        </motion.div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-200/50 dark:border-gray-700/50"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                    <TagIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {editingCategory ? 'Edit Category' : 'New Category'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {editingCategory ? 'Update category details' : 'Create a new expense category'}
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </motion.button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    placeholder="e.g., Groceries"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Icon</label>
                  <div className="grid grid-cols-8 gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 max-h-48 overflow-y-auto">
                    {PRESET_ICONS.map((iconOption) => (
                      <motion.button
                        key={iconOption.name}
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setFormData({ ...formData, icon: iconOption.name })}
                        className={`text-2xl p-2 rounded-xl transition-all ${
                          formData.icon === iconOption.name
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/30'
                            : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        title={iconOption.name}
                      >
                        {iconOption.emoji}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Color</label>
                  <div className="flex flex-wrap gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                    {PRESET_COLORS.map((color) => (
                      <motion.button
                        key={color}
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                          formData.color === color ? 'ring-2 ring-offset-2 ring-gray-800 dark:ring-white' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      >
                        {formData.color === color && <CheckIcon className="h-5 w-5 text-white" />}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Preview</p>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg"
                      style={{
                        backgroundColor: `${formData.color || '#6B7280'}20`,
                        boxShadow: `0 10px 25px -5px ${formData.color || '#6B7280'}30`
                      }}
                    >
                      {getCategoryIcon(formData.icon || 'other')}
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formData.name || 'Category Name'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium shadow-lg shadow-indigo-500/30 hover:shadow-xl transition-all"
                  >
                    {editingCategory ? 'Update' : 'Create'} Category
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Categories;

